import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { ArrowRight, AlertCircle, Eye, EyeOff, Moon, Sun } from 'lucide-react';
import StarrySky from '@/components/StarrySky';

export default function LoginPage() {
  const { user, signInWithOtp, signInWithPassword, effectiveRole } = useAuth();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'magic' | 'password'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const isDark = resolved === 'dark';

  useEffect(() => {
    if (!user) return;
    const role = effectiveRole || user.role;
    const dest =
      role === 'partner_principal' || role === 'partner_staff'
        ? '/partner'
        : role === 'account_manager'
          ? '/leads'
          : '/dashboard';
    navigate(dest, { replace: true });
  }, [user, effectiveRole, navigate]);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await signInWithOtp(email);
    setLoading(false);
    if (result.success) {
      setSent(true);
    } else {
      setError(result.error || 'Failed to send login link');
    }
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await signInWithPassword(email, password);
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'Invalid credentials');
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 relative overflow-hidden dot-grid"
      style={{ background: isDark ? '#080909' : 'var(--bg-surface)' }}
    >
      {/* Theme toggle — top-right corner */}
      <button
        onClick={toggle}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        className="absolute top-5 right-5 z-10 p-2 rounded-lg transition-all duration-200"
        style={{
          background: 'transparent',
          border: '1px solid var(--border-separator)',
          color: 'var(--text-muted)',
          cursor: 'pointer',
        }}
      >
        {isDark ? <Sun size={16} strokeWidth={1.5} /> : <Moon size={16} strokeWidth={1.5} />}
      </button>

      {/* Light mode ambient haze blobs */}
      {!isDark && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute animate-blob"
            style={{
              top: '-15%',
              left: '-10%',
              width: '55%',
              height: '55%',
              borderRadius: '50%',
              background: 'radial-gradient(ellipse, rgba(180,220,170,0.18) 0%, transparent 70%)',
              animationDuration: '22s',
            }}
          />
          <div
            className="absolute animate-blob"
            style={{
              bottom: '-15%',
              right: '-10%',
              width: '50%',
              height: '50%',
              borderRadius: '50%',
              background: 'radial-gradient(ellipse, rgba(160,140,210,0.12) 0%, transparent 70%)',
              animationDuration: '28s',
              animationDelay: '8s',
            }}
          />
          <div
            className="absolute animate-blob"
            style={{
              top: '30%',
              left: '35%',
              width: '40%',
              height: '40%',
              borderRadius: '50%',
              background: 'radial-gradient(ellipse, rgba(200,230,200,0.10) 0%, transparent 70%)',
              animationDuration: '18s',
              animationDelay: '4s',
            }}
          />
        </div>
      )}
      {/* Starry sky — only visible in dark mode */}
      {isDark && (
        <>
          <StarrySky />
          <div
            style={{
              position:      'absolute',
              inset:         0,
              background:    'radial-gradient(ellipse 60% 50% at 10% 15%, rgba(113,180,112,0.07) 0%, transparent 55%)',
              pointerEvents: 'none',
              zIndex:        1,
            }}
          />
        </>
      )}

      {/* Ambient top glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-120px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '720px',
          height: '420px',
          background: isDark
            ? 'radial-gradient(ellipse at 50% 0%, rgba(200,200,210,0.06), transparent 70%)'
            : 'radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.03), transparent 70%)',
        }}
      />

      <div className="relative w-full max-w-[440px] animate-fade-up">

        {/* Wordmark — letter reveal */}
        <div className="text-center mb-10">
          <h1
            className="text-[36px] font-semibold"
            style={{
              fontFamily: "'StackSansNotch', sans-serif",
              color: 'var(--text-primary)',
              animation: 'wordmark-spacing 1s cubic-bezier(0.16,1,0.3,1) forwards',
              letterSpacing: '0.04em',
            }}
          >
            {'converze'.split('').map((letter, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  opacity: 0,
                  animation: `letter-lift 0.45s cubic-bezier(0.16,1,0.3,1) ${i === 0 ? '0s' : `${0.04 + (i - 1) * 0.018}s`} forwards`,
                }}
              >
                {letter}
              </span>
            ))}
          </h1>
        </div>

        {/* Card with aura rim light */}
        <div className="login-rim-wrap">
          <div className="login-rim-inner px-10 py-10">

            {sent ? (
              <div className="text-center py-6 animate-fade-in">
                <div className="mx-auto mb-5 w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ border: '1px solid var(--border-primary)', background: 'var(--bg-input)' }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                </div>
                <h2 className="text-[15px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                  Check your inbox
                </h2>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  We sent a login link to {email}
                </p>
                <button
                  onClick={() => { setSent(false); setEmail(''); }}
                  className="mt-6 text-xs transition-colors hover-muted"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-[15px] font-semibold mb-7 text-center" style={{ color: 'var(--text-secondary)' }}>
                  Sign in
                </h2>

                {error && (
                  <div className="mb-5 flex items-center gap-2 p-3 rounded-lg border text-sm animate-scale-in"
                    style={{ borderColor: 'var(--danger-border)', background: 'var(--danger-bg)', color: 'var(--danger-text)' }}
                  >
                    <AlertCircle size={14} className="shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={mode === 'magic' ? handleMagicLink : handlePassword}>
                  <div className="mb-4">
                    <label
                      className="block mb-1.5"
                      style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      required
                      className="w-full px-4 py-3 text-sm rounded-xl outline-none transition-all"
                      style={{
                        background: 'var(--bg-input)',
                        border: '1px solid var(--border-primary)',
                        color: 'var(--text-primary)',
                        fontFamily: 'inherit',
                      }}
                      onFocus={e => { e.target.style.borderColor = 'var(--border-active)'; e.target.style.background = 'var(--bg-input-focus)'; }}
                      onBlur={e => { e.target.style.borderColor = 'var(--border-primary)'; e.target.style.background = 'var(--bg-input)'; }}
                    />
                  </div>

                  {mode === 'password' && (
                    <div className="mb-4">
                      <label
                        className="block mb-1.5"
                        style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}
                      >
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          required
                          className="w-full pl-4 pr-10 py-3 text-sm rounded-xl outline-none transition-all"
                          style={{
                            background: 'var(--bg-input)',
                            border: '1px solid var(--border-primary)',
                            color: 'var(--text-primary)',
                            fontFamily: 'inherit',
                          }}
                          onFocus={e => { e.target.style.borderColor = 'var(--border-active)'; e.target.style.background = 'var(--bg-input-focus)'; }}
                          onBlur={e => { e.target.style.borderColor = 'var(--border-primary)'; e.target.style.background = 'var(--bg-input)'; }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors hover-muted"
                          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full mt-5 flex items-center justify-center gap-2 py-3 rounded-xl text-[13px] font-medium transition-all disabled:opacity-50 login-submit-btn"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--border-separator)', borderTopColor: 'var(--text-secondary)' }} />
                    ) : (
                      <>
                        {mode === 'magic' ? 'Send login link' : 'Sign in'}
                        <ArrowRight size={15} strokeWidth={2.5} />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-4 text-center">
                  <button
                    onClick={() => { setMode(mode === 'magic' ? 'password' : 'magic'); setError(''); }}
                    className="text-xs transition-colors hover-muted"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {mode === 'magic' ? 'Use password instead' : 'Use magic link instead'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
